import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ImageUploader from "../components/ImageUploader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const inputCls = "bg-[#121212] border-axe-border rounded-xl text-white h-12 px-4";
const EMPTY = { type: "image", url: "", caption: "", platform: "" };

export default function GalleryEditor() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, ConfirmModal] = useConfirm();

  async function refresh() {
    const { data } = await api.get("/gallery");
    setItems(data);
  }
  useEffect(() => {
    refresh();
  }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        url: form.url,
        caption: form.caption || "",
        platform: form.platform || "",
      };
      if (form._editId) await api.put(`/gallery/${form._editId}`, payload);
      else await api.post("/gallery", payload);
      toast.success("Saved");
      setForm(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!(await confirm("Delete this item?"))) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="gallery-editor">
      <ConfirmModal />
      <div className="flex justify-end mb-5">
        <button onClick={() => setForm({ ...EMPTY })} className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover" data-testid="gallery-new">
          <Plus size={14} /> Add Item
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((i) => (
          <div key={i.id} className="rounded-2xl border border-axe-border bg-axe-surface overflow-hidden" data-testid={`gallery-row-${i.id}`}>
            <div className="relative aspect-[4/3] bg-axe-bg">
              {i.type === "image" ? (
                <img src={i.url} alt="" className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-axe-purple font-display font-bold text-lg">
                  ▶ VIDEO
                </div>
              )}
              <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-black/60 text-white border border-white/10">
                {i.type}{i.platform ? ` · ${i.platform}` : ""}
              </div>
            </div>
            <div className="p-4">
              <div className="text-sm text-neutral-300 line-clamp-2 min-h-[2.5em]">{i.caption || "—"}</div>
              <div className="mt-3 flex gap-3">
                <button onClick={() => setForm({ ...i, _editId: i.id })} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`gallery-edit-${i.id}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => remove(i.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`gallery-delete-${i.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No gallery items yet.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="gallery-modal">
          <form onSubmit={save} className="relative w-full max-w-xl bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 space-y-5 my-10">
            <button type="button" onClick={() => setForm(null)} className="absolute right-4 top-4 p-2 text-neutral-400 hover:text-white" aria-label="Close"><X size={18} /></button>
            <h3 className="font-display font-black text-2xl">{form._editId ? "Edit item" : "New item"}</h3>

            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="gallery-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-axe-surface border-axe-border text-white">
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="video">Video (embed)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.type === "image" ? (
              <div>
                <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Photo</Label>
                <div className="mt-2"><ImageUploader value={form.url} onChange={(url) => setForm({ ...form, url })} aspect="aspect-video" testId="gallery-image-uploader" /></div>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Embed URL (YouTube /embed/ or Twitch)</Label>
                  <Input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={`mt-2 ${inputCls}`} placeholder="https://www.youtube.com/embed/..." data-testid="gallery-url" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Platform</Label>
                  <Select value={form.platform || "youtube"} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className={`mt-2 ${inputCls}`} data-testid="gallery-platform"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-axe-surface border-axe-border text-white">
                      <SelectItem value="youtube">YouTube</SelectItem>
                      <SelectItem value="twitch">Twitch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Caption</Label>
              <Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="gallery-caption" />
            </div>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setForm(null)} className="rounded-full border border-axe-border px-6 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-bg">Cancel</button>
              <button type="submit" disabled={saving} className="rounded-full bg-axe-purple px-7 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover disabled:opacity-50" data-testid="gallery-save">
                {saving ? "Saving…" : form._editId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
